import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  IconButton,
  Button,
  Grid,
  useMediaQuery,
} from "@mui/material";
import styled from "@emotion/styled";
import {
  generateTestId,
  getMediaPath,
} from "app/functions";
import {
  is_valid_israeli_id_number,
  REGEX_PATTERNS,
  VALIDATION_MESSAGES,
} from "app/validation/validation";
import { useSelector } from "react-redux";
import HotTextInput from "components/forms/HotTextInput";
import HotTextArea from "components/forms/HotTextArea";
import HotCheckbox from "components/forms/HotCheckbox";
import Recaptcha from "components/services/google/recaptcha";
import HotActionButton from "components/action-button/hotActionButton";
import {useExecuteFlowMutation } from "redux/api/relations.api";
import CloseIcon from "@mui/icons-material/HighlightOffSharp";
import Loader from "components/common/loader";
import { createPath } from "theme";
import { useTheme } from "@emotion/react";
import HtmlContent from "components/html/HtmlContent";
import { useLocation } from 'react-router-dom';
import ReactHtmlParser from "react-html-parser";

const SectionWrapper = styled(Box)(({ theme }) => ({
  position: "relative",
  height: "100%",
  background: "linear-gradient(196deg, #8533EB 11.28%, #4001BF 98.81%)",
  padding: "20px",
  [theme.breakpoints.up("md")]: {
    height: "fit-content",
    padding: "0px 50px",
    backgroundSize: "100% auto",
    backgroundRepeat: "no-repeat",
  },
  [theme.breakpoints.up("lg")]: {
    padding: "10px 100px 0px 100px",
  }
}));

const FormWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "left",
  justifyContent: "center",
  flexDirection: "column",
  filter: 'drop-shadow(0px 4.615px 69.231px rgba(45, 85, 199, 0.25))',
  zIndex: 1,
  borderRadius: "24px",
  backgroundColor: "white",
  paddingBottom: "0px",
  [theme.breakpoints.up("md")]: {
    width: "fit-content",
    minHeight: "fit-content",
    marginTop: '20px',
    backgroundColor: "transparent",
  },
  [theme.breakpoints.up("lg")]: {
    marginTop: '20px',
  },
}));


const FormContainer = styled(Box)(({ theme }) => ({
  borderRadius: "24px",
  padding: "0px 20px",
  height: "100%",
  marginTop: "10px",
  background: "white",
  justifyContent: "space-between",
  flexDirection: "column",
  display: "flex",
  [theme.breakpoints.up("md")]: {
    width: "850px",
},
[theme.breakpoints.up("lg")]: {
    width: "900px",
  },
}));


// Adjust the FormGrid to handle row placement based on field widths
const FormGrid = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "20px",
  [theme.breakpoints.up("md")]: {
    display: "flex",
    flexWrap: "wrap",
    width: "100%",
    alignItems: "start",
    flexDirection: "row",
  },
}));

const FormFieldContainer = styled("div")(({ theme }) => ({
  position: "relative",
  padding: "20px 0",
  marginTop: "10px",
  background: "white",
  [theme.breakpoints.up("md")]: {
    background: "transparent",
  },
}));


const CheckboxesContainer = styled(Box)(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    marginRight: 200,
  },
}));

const NameFieldsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "20px",
  [theme.breakpoints.up("md")]: {
    flexDirection: "row",
  },
}));

const ButtonContainer = styled("div")(({ theme }) => ({
  margin: "20px",
  [theme.breakpoints.up("md")]: {
    width: "173px",
    right: 0,
    bottom: 0,
    position: "absolute",
    margin: '0px',
    marginBottom: '20px',
  },
}));

const handleMobileButtonClick = (url) => {
  window.open(url, '_blank', 'noopener,noreferrer');
}

const UserApplicationPopup = ({
  disabled,
  isDialogOpen,
  setIsDialogOpen,
  title,
  testID,
  onClose,
  children,
  hideOkButton,
  onSubmit,
}) => {
  return (
    <Dialog
      open={isDialogOpen}
      fullWidth
      maxWidth="sm"
      sx={{
        "& .MuiDialog-paper": {
          padding: "10px 30px",
          position: "relative",
          marginTop: "30px",
        },
      }}
      onClose={() => {
        setIsDialogOpen(false);
        onClose();
      }}
    >
      <IconButton
        aria-label="close"
        onClick={() => {
          setIsDialogOpen(false);
          onClose();
        }}
        sx={{
          position: "absolute",
          color: (theme) => theme.palette.grey[900],
          padding: "2px",
          left:4,
          top:4
        }}
      >
        <CloseIcon fontSize="large" />
      </IconButton>
      <DialogTitle testID={generateTestId(testID, "popup-title")}>
        {title}
      </DialogTitle>
      <DialogContent>
      {children}
      </DialogContent>
      {!hideOkButton && <DialogActions disableSpacing>
        <HotActionButton
          size="large"
          onClick={onSubmit}
          disabled={disabled}
          testID={generateTestId(testID, "popup-button")}
          sx={{
            width: "20%",
            margin: "0 auto",
          }}
        >
          אישור
        </HotActionButton>
      </DialogActions>}
    </Dialog>
  );
};

const formatDefaultWorkPlace = (workplace) => {
  return {
    value: workplace.id,
    label: workplace.text,
  }
}

const FormFieldType = {
    ID: 1,
    FIRST_NAME: 2,
    LAST_NAME: 3,
    EMAIL: 4,
    PHONE: 5,
    FULL_NAME: 6,
    TEXT_FIELD: 7,
    DATE: 8,
    NUMBER: 9,
}

const FormFieldWidthSize = {
    HALF: 0,
    FULL: 1,
}

const calculateFormHeight = (fields, checkboxes) => {
  const baseHeight = 250;
  const baseCheckboxesNumber = 1;

  const additionalHeightPerField = 50;
  
  const additionalHeightPerTextAreaField = 100;

  const additionalHeightPerCheckbox = 100;

  let fullWidthFieldCount = 0;
  let isPreviousHalfWidth = false;
  let textAreaFieldCount = 0;

  fields.forEach(field => {
    if (field.widthSize === FormFieldWidthSize.FULL) {
      fullWidthFieldCount += 1;
      isPreviousHalfWidth = false;
    } else if (field.widthSize === FormFieldWidthSize.HALF) {
      if (isPreviousHalfWidth) {
        // Pair with the previous half-width field
        isPreviousHalfWidth = false;
      } else {
        // Start a new row for this half-width field
        fullWidthFieldCount += 1;
        isPreviousHalfWidth = true;
      }
    }
    // Count text area fields separately
    if (field.type === FormFieldType.TEXT_FIELD) {
      textAreaFieldCount += 1;
    }
  });

  const checkboxesAdditionalCount = checkboxes.length - baseCheckboxesNumber;
  const fullWidthFieldCountHalf = Math.ceil(fullWidthFieldCount / 2);
  const totalHeight = baseHeight + (fullWidthFieldCountHalf * additionalHeightPerField) + (textAreaFieldCount * additionalHeightPerTextAreaField) + (checkboxesAdditionalCount * additionalHeightPerCheckbox);

  return totalHeight;
};

const splitFieldsIntoColumns = (fields) => {
  const columns = [[], []];
  let currentColumn = 0;
  let isPreviousHalfWidth = false;

  fields.forEach((field, index) => {
    if (field.widthSize === FormFieldWidthSize.FULL) {
      columns[currentColumn].push(field);
      isPreviousHalfWidth = false;
      // Switch to the other column for the next full-width field
      currentColumn = currentColumn === 0 ? 1 : 0;
    } else if (field.widthSize === FormFieldWidthSize.HALF) {
      if (isPreviousHalfWidth) {
        columns[currentColumn].push(field);
        isPreviousHalfWidth = false;
        // Switch to the other column for the next field
        currentColumn = currentColumn === 0 ? 1 : 0;
      } else {
        columns[currentColumn].push(field);
        isPreviousHalfWidth = true;
      }
    }
  });

  return columns;
};

const FormSection = ({
  data,
  testID,
  ref,
}) => {
    const {
        formFields,
        formCheckboxes = [],
        formTitle,
        formSubtitle,
        formButtonText,
        formLegalText,
        formCheckboxErrorText,
        formBackgroundImage,
        formSlug,
        formApprovalPopup = {},
    } = data;

  const workplaces = useSelector((state) => state.config.WorkPlaces);
  const text = useSelector((state) => state.config.Translations);
  const defaultValues = data?.defaultValues || {};
    
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [executeFlow, { isLoading: isFlowLoading, reset: resetExecuteFlow}] = useExecuteFlowMutation();


  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);

  const [isMessageDialogOpen, setIsMessageDialogOpen] = useState(false);
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [formApprovalPopupContent, setFormApprovalPopupContent] = useState(formApprovalPopup);

  const [relevantFields, setRelevantFields] = useState(formFields);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  
  const fieldsDefaultValues = formFields.reduce((acc, field) => {
    if (field.constantValue) {
      acc[field.name] = field.constantValue;
    }
    if (searchParams.get(field.name) && !field.constantValue) {
      acc[field.name] = searchParams.get(field.name);
    } 
    if (!field.constantValue && !searchParams.get(field.name)) {
      acc[field.name] = "";
    }
    return acc;
  }, {});

  // Create default values for checkboxes
  const checkboxesDefaultValues = formCheckboxes.reduce((acc, checkbox) => {
    acc[checkbox.name] = false; // Default to unchecked
    return acc;
  }, {});

  // Create default values for fields and checkboxes
  const mergedDefaultValues = {
    ...fieldsDefaultValues,
    ...checkboxesDefaultValues,
  };

  const { handleSubmit, control, watch, getValues, reset } = useForm({
    defaultValues: {
      message: "",
      agreement: false,
      marketingConsent: false,
      slug: formSlug,
      ...mergedDefaultValues,
    },
  });

  const onApproval = () => {
    setIsFormDialogOpen(true);
  };

  const resetForm = () => {
    reset();
    setIsFormDialogOpen(false);
    setIsMessageDialogOpen(false);
    setErrorMessage("");
    resetExecuteFlow();   
  };  

  const submitUserApplication = async () => {
    const formData = getValues();
    const response = await executeFlow(formData);
    if (response.data.isValid) {
      setIsFormDialogOpen(true);
    } else {
      setErrorMessage(response.data.message);
      setIsErrorDialogOpen(true);
    }
};

  return (
    <SectionWrapper
      id="landing-page-section"
      ref={ref}
      testID={generateTestId(testID, "root")}
      sx={{
        backgroundImage: {md:`url(${getMediaPath(formBackgroundImage)})`},
      }}
    >
      <FormWrapper
        testID={generateTestId(testID, "formWrapper")}
        id="landing-page-container-wrapper"
      >
        <FormContainer
          testID={generateTestId(testID, "formContainer")}
          id="landing-page-container"
          sx={{
            height: {
              md: calculateFormHeight(relevantFields, formCheckboxes) + 15,
            },
            clipPath: {
              md: createPath(1024, calculateFormHeight(relevantFields, formCheckboxes) + 15),
            }
          }}
        >
          <FormFieldContainer
            id="landing-page"
            testID={generateTestId(testID, "form")}
            onSubmit={()=>{
              handleSubmit(submitUserApplication)();
            }}
          >
            <div>
              <Typography
                variant="title"
                testID={generateTestId(testID, "title")}
                sx={{
                  marginBottom: "12px",
                }}
              >
                {formTitle}
              </Typography>
              <Typography
                variant="subtitle"
                sx={{
                  marginBottom: "20px",
                }}
                testID={generateTestId(testID, "subtitle")}
              >
                {formSubtitle}
              </Typography>
            </div>
            <Grid container spacing={{xs: 2, md: 6}} testID={generateTestId(testID, "formGrid")} sx={{ width: '100%' }}>
              {splitFieldsIntoColumns(relevantFields).map((column, columnIndex) => (
                <Grid item xs={12} md={6} key={`column-${columnIndex}`}>
                  <Grid container spacing={2}>
                    {column.map((field) => (
                      <Grid item xs={12} md={field.widthSize === FormFieldWidthSize.FULL ? 12 : 6} key={field.name}>
                        {field.type === FormFieldType.ID && (
                          <HotTextInput
                            name={field.name}
                            testID={generateTestId(testID, `input-${field.name}`)}
                            control={control}
                            label={field.text}
                            required={field.isRequired}
                            rules={{
                              ...(field.isRequired && { required: VALIDATION_MESSAGES.REQUIRED }),
                              validate: {
                                isValidId: (value) =>
                                  is_valid_israeli_id_number(value) ||
                                  VALIDATION_MESSAGES.ID,
                              },
                            }}
                            InputProps={{
                              defaultValue: fieldsDefaultValues[field.name],
                            }}
                          />
                        )}
                        {field.type === FormFieldType.FIRST_NAME && (
                          <HotTextInput
                            name={field.name}
                            testID={testID}
                            control={control}
                            label={field.text}
                            required={field.isRequired}
                            rules={{
                              ...(field.isRequired && { required: VALIDATION_MESSAGES.REQUIRED }),
                              pattern: {
                                value: REGEX_PATTERNS.FIRST_NAME,
                                message: VALIDATION_MESSAGES.FIRST_NAME,
                              },
                            }}
                            InputProps={{
                              defaultValue: fieldsDefaultValues[field.name],
                            }}
                          />
                        )}
                        {field.type === FormFieldType.LAST_NAME && (
                          <HotTextInput
                            name={field.name}
                            testID={testID}
                            control={control}
                            label={field.text}
                            required={field.isRequired}
                            rules={{
                              ...(field.isRequired && { required: VALIDATION_MESSAGES.REQUIRED }),
                              pattern: {
                                value: REGEX_PATTERNS.LAST_NAME,
                                message: VALIDATION_MESSAGES.LAST_NAME,
                              },
                            }}
                            InputProps={{
                              defaultValue: fieldsDefaultValues[field.name],
                            }}
                          />
                        )}
                        {field.type === FormFieldType.EMAIL && (
                          <HotTextInput
                            name={field.name}
                            testID={testID}
                            control={control}
                            label={field.text}
                            required={field.isRequired}
                            rules={{
                              ...(field.isRequired && { required: VALIDATION_MESSAGES.REQUIRED }),
                              pattern: {
                                value: REGEX_PATTERNS.EMAIL,
                                message: VALIDATION_MESSAGES.EMAIL,
                              },
                            }}
                            InputProps={{
                              defaultValue: fieldsDefaultValues[field.name],
                            }}
                          />
                        )}
                        {field.type === FormFieldType.PHONE && (
                          <HotTextInput
                            name={field.name}
                            testID={testID}
                            control={control}
                            label={field.text}
                            required={field.isRequired}
                            rules={{
                              ...(field.isRequired && { required: VALIDATION_MESSAGES.REQUIRED }),
                              pattern: {
                                value: REGEX_PATTERNS.CELL,
                                message: VALIDATION_MESSAGES.CELL,
                              },
                            }}
                            InputProps={{
                              defaultValue: fieldsDefaultValues[field.name],
                            }}
                          />
                        )}
                        {field.type === FormFieldType.FULL_NAME && (
                          <HotTextInput
                            name={field.name}
                            testID={testID}
                            control={control}
                            label={field.text}
                            required={field.isRequired}
                            rules={{
                              ...(field.isRequired && { required: VALIDATION_MESSAGES.REQUIRED }),
                              pattern: {
                                value: REGEX_PATTERNS.NAME,
                                message: VALIDATION_MESSAGES.NAME,
                              },
                            }}
                            InputProps={{
                              defaultValue: fieldsDefaultValues[field.name],
                            }}
                          />
                        )}
                        {field.type === FormFieldType.TEXT_FIELD && (
                          <HotTextArea
                            name={field.name}
                            testID={testID}
                            control={control}
                            label={field.text}
                            required={field.isRequired}
                            rules={{
                              ...(field.isRequired && { required: VALIDATION_MESSAGES.REQUIRED }),
                              pattern: {
                                value: REGEX_PATTERNS.NAME,
                                message: VALIDATION_MESSAGES.NAME,
                              },
                            }}
                            InputProps={{
                              defaultValue: fieldsDefaultValues[field.name],
                            }}
                          />
                        )}
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </FormFieldContainer>
          <CheckboxesContainer>
            {formCheckboxes.map((checkbox) => (
              <HotCheckbox
                key={checkbox.name}
                name={checkbox.name}
                testID={testID}
                linksNotClickedErrorText={formCheckboxErrorText}
                control={control}
                rules={{
                  required: VALIDATION_MESSAGES.REQUIRED,
                }}
                sx={{
                  '& .MuiTypography-root': {
                    fontSize: {
                      xs: '12px',
                      sm: '14px',
                    },
                  },
                }}
                label={checkbox.text}
              />
            ))}
          </CheckboxesContainer>
        </FormContainer>
        <Recaptcha isFunction={false} enterprise={true} id="recaptcha-container" />
        <ButtonContainer >
            <HotActionButton
              size="large"
              onClick={()=>{
                handleSubmit(submitUserApplication)();
              }}
              fullWidth
              testID={testID}
              loading={isFlowLoading}
              disabled={isFlowLoading}
            >
              {formButtonText}
            </HotActionButton>
        </ButtonContainer>    
        <Box sx={{
          marginTop: '20px',
          fontSize: '12px',
          [theme.breakpoints.down("md")]: {
            color: 'white',
          },
          [theme.breakpoints.up("md")]: {
            maxWidth: '80%',
          },
          [theme.breakpoints.up("lg")]: {
            fontSize: '14px',
          },
          [theme.breakpoints.up("xl")]: {
            maxWidth: '60%',
          },
        }}>
          <HtmlContent content={formLegalText} />
        </Box>
      {isFlowLoading && <Loader />}

      <UserApplicationPopup
        onClose={() => resetForm()}
        isDialogOpen={isFormDialogOpen}
        setIsDialogOpen={resetForm}
        title={
          executeFlow?.isValid
            ? text.landing_lead_sent_title
            : ""
        }
        testID={testID}
        disabled={false}
        onSubmit={() => resetForm()}
        hideOkButton={true}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="title" sx={{ marginBottom: "0px", textAlign: 'center' }}>
            {ReactHtmlParser(formApprovalPopupContent.title)}
          </Typography>
          {!isMobile && formApprovalPopupContent.desktopImage && <img src={getMediaPath(formApprovalPopupContent.desktopImage)} alt="approval" style={{ marginBottom: '14px', justifyContent: 'center', alignContent: 'center', alignItems: 'center', maxHeight: '550px', width: 'auto', margin: '0 auto' }}/> }
          {isMobile && formApprovalPopupContent.link && <HotActionButton
            size="large"
            onClick={() => handleMobileButtonClick(formApprovalPopupContent.link)}
            testID={generateTestId(testID, "approval-popup-button")}
            sx={{
              width: "80%",
              textAlign: "center",
              justifyContent: "center",
              alignItems: "center",
              display: "flex",
              margin: "0 auto",
            }}
          >
            {ReactHtmlParser(formApprovalPopupContent.mobileButtonText)}
          </HotActionButton>
          }
        </div>
      </UserApplicationPopup>

      <Dialog
        open={isErrorDialogOpen}
        onClose={() => setIsErrorDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>שגיאה</DialogTitle>
        <DialogContent>
          <Typography>{errorMessage}</Typography>
        </DialogContent>
        <DialogActions disableSpacing>
          <HotActionButton
            size="large"
            onClick={() => setIsErrorDialogOpen(false)}
            testID={generateTestId(testID, "error-popup-button")}
            sx={{
              width: "20%",
              margin: "0 auto",
            }}
          >
            סגור
          </HotActionButton>
        </DialogActions>
      </Dialog>
    </FormWrapper>
    </SectionWrapper>
  );
};

export default FormSection; 